import { Organization, Story } from "chq";

import { ElementState, Preview } from "@creatomate/preview";

import { getSubdomain } from "app/utils";
import CheckAspectRatio from "app/utils/CheckAspectRatio";

import loadOrganizationFonts from "../loadOrganizationFonts";
import validImageUrl from "./validImageUrl";
import { IMAGE_TYPE, VIDEO_TYPE } from "../constants";
import getWidthAndHeightFromAspectRatio from "../getWidthAndHeightFromAspectRatio";

interface GetSourceParams {
  organization: Organization;
  preview: Preview;
  story?: Story;
  aspectRatio?: number;
  original?: boolean;
  mediaElements?: ElementState[];
  outputFormat?: string;
  selectedVideos?: File[];
}

const getVideoDuration = (videoFile: File): Promise<number> => new Promise(resolve => {
  const videoElement = document.createElement("video");
  videoElement.src = URL.createObjectURL(videoFile);
  videoElement.preload = "metadata";

  videoElement.onloadedmetadata = () => {
    const { duration } = videoElement;
    URL.revokeObjectURL(videoElement.src);
    resolve(duration);
  };
});

const getStoryElements = async (preview: Preview, selectedVideos: File[]) => {
  const storyElements = [];
  for (let i = 0; i < selectedVideos.length; i += 1) {
    const media = selectedVideos[i];
    const sourceUrl = `https://${getSubdomain()}.culturehq.com/${Date.now()}`;
    const fileUrl = URL.createObjectURL(media);
    // eslint-disable-next-line no-await-in-loop
    await preview.cacheAsset(sourceUrl, media);
    const name = `media-${media.name}-fileUrl-${fileUrl}`;
    // eslint-disable-next-line no-await-in-loop
    const videoDuration = await getVideoDuration(media);

    storyElements.push({
      type: VIDEO_TYPE,
      name,
      track: 1,
      source: sourceUrl,
      duration: videoDuration,
      fit: "contain",
      width: "100%"
    });
  }

  return storyElements;
};

const getStoryElement = async (preview: Preview, story: Story) => {
  const isLocalfile = (story.media instanceof File || story.media instanceof Blob);
  let { mediaType } = story.media;
  let sourceUrl;
  let name;

  // we are validatiing if it is a local file, if not, it is a Unsplash file
  if (story.media instanceof File || story.media instanceof Blob) {
    const fileUrl = URL.createObjectURL(story.media);
    mediaType = story.media.type.startsWith(VIDEO_TYPE) ? VIDEO_TYPE : IMAGE_TYPE;
    sourceUrl = `https://${getSubdomain()}.culturehq.com/${Date.now()}`;
    await preview.cacheAsset(sourceUrl, story.media);
    name = `media-${story.media instanceof File ? story.media.name : mediaType}-fileUrl-${fileUrl}`;
  } else if (story.media.urls) {
    mediaType = "image";
    sourceUrl = story.media.urls.raw;
    name = `media-${story.media.alt_description}`;
  } else if (story.media.mediaType === VIDEO_TYPE) {
    name = `media-${story.media.url}`;
    sourceUrl = story.media.shareableUrl;
  } else {
    name = `media-${story.creator.name}'s ${story.media.mediaType}`;
    sourceUrl = await validImageUrl(story.media);
  }

  return {
    type: mediaType,
    name: mediaType === IMAGE_TYPE ? name.replace(/-fileUrl-blob:.*$/, "") : name,
    track: 1,
    time: 0,
    duration: isLocalfile ? story.videoDuration : story.media.duration,
    source: sourceUrl,
    fit: "contain",
    width: "100%"
  };
};

const camelToSnake = (str: string): string => (
  str.replace(/([a-z])([A-Z])/g, "$1_$2").toLowerCase()
);

const convertKeysToSnakeCase = (obj: any): any => {
  if (Array.isArray(obj)) {
    return obj.map(convertKeysToSnakeCase);
  }

  if (obj !== null && typeof obj === "object") {
    return Object.keys(obj).reduce((acc, key) => {
      const newKey = camelToSnake(key);
      acc[newKey] = convertKeysToSnakeCase(obj[key]);
      return acc;
    }, {} as Record<string, any>);
  }
  return obj;
};

const getDefaultSource = async ({
  organization,
  preview,
  story,
  aspectRatio,
  original,
  mediaElements = [],
  outputFormat = "mp4",
  selectedVideos = []
}: GetSourceParams): Promise<Record<string, any>> => {
  if (story?.media?.mediaJson && !original && Object.keys(story.media.mediaJson).length > 0) {
    const { mediaJson } = story.media;
    if (typeof mediaJson === "object") return convertKeysToSnakeCase(mediaJson);
    return convertKeysToSnakeCase(JSON.parse(mediaJson));
  }

  let elements: any = [];
  let mediaType = VIDEO_TYPE;
  if (mediaElements.length > 0) {
    mediaElements.forEach(element => {
      elements.push({
        ...element.source,
        track: 1,
        x: "50%",
        y: "50%",
        width: "100%",
        height: "100%",
        border_radius: 0,
        clip: false
      });
    });
  } else if (story) {
    if (selectedVideos.length > 1) {
      const storyElements = await getStoryElements(preview, selectedVideos);
      elements = storyElements;
    } else {
      const storyElement = await getStoryElement(preview, story);
      mediaType = storyElement.type;
      elements.push(storyElement);
    }
  }

  const {
    width: initialWidth, height: initialHeight
  } = getWidthAndHeightFromAspectRatio(aspectRatio);
  const { width, height } = CheckAspectRatio.calculate({
    width: story?.media?.width || initialWidth,
    height: story?.media?.height || initialHeight
  });

  const getOutputFormat = () => {
    if (outputFormat === "png") return "png";
    return mediaType === VIDEO_TYPE ? "mp4" : "png";
  };

  return {
    output_format: getOutputFormat(),
    width,
    height,
    frame_rate: 25,
    fonts: loadOrganizationFonts(organization),
    fill_color: "#ffffff",
    elements
  };
};

export default getDefaultSource;
